import { useEffect, useMemo, useState } from 'react';
import { fetchPowerBISemanticData } from '../services/powerbiService';
import authService from '../services/authService';

const VCCM_MATRIX_PREVIEW_LIMIT = 12;
const VCCM_TA_TEAM_NAME = 'Immunization - VCCM';
const GAVI_INVENTORY_TABLE = process.env.REACT_APP_GAVI_INVENTORY_TABLE_NAME || 'gavi_inventory_data';
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://uni-func-pd-thrive360.azurewebsites.net/api';

const normalizeKey = (value) => String(value || '').trim().toUpperCase();
const normalizeText = (value = '') => String(value || '').trim().toLowerCase();

const parseMetricNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const normalizedValue = typeof value === 'string'
    ? value.replace(/[%,"\s]/g, '')
    : value;
  const numericValue = Number(normalizedValue);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const toPercentPoints = (numericValue) => (
  Math.abs(numericValue) <= 2 ? numericValue * 100 : numericValue
);

const formatPercent = (value) => {
  const numericValue = parseMetricNumber(value);
  if (numericValue === null) return null;
  return `${toPercentPoints(numericValue).toFixed(1)}%`;
};

const formatNumber = (value) => {
  const numericValue = parseMetricNumber(value);
  return numericValue === null ? null : Math.round(numericValue).toLocaleString();
};

const formatScore = (value) => {
  const numericValue = parseMetricNumber(value);
  return numericValue === null ? (value ? String(value) : null) : numericValue.toFixed(1);
};

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('en-GB');
};

const getRowValue = (row, names) => {
  const normalizedNames = names.map((name) => normalizeKey(name).replace(/[[\]]/g, ''));
  const matchingKey = Object.keys(row || {}).find((key) => normalizedNames.includes(normalizeKey(key).replace(/[[\]]/g, '')));
  return matchingKey ? row[matchingKey] : undefined;
};

const normalizeColumn = (name) => {
  const raw = String(name || '').trim();
  const bracketMatch = raw.match(/\[([^\]]+)\]\s*$/);
  return String(bracketMatch ? bracketMatch[1] : raw).trim().toLowerCase();
};

const findColumnKey = (columns, names) => columns.find((col) => names.includes(normalizeColumn(col))) || null;
const getRowText = (row, columnKey) => (columnKey ? String(row?.[columnKey] || '').trim() : '');
const getTaRequestKey = (row, index, titleColumnKey) => `${getRowText(row, titleColumnKey) || 'vccm-ta-request'}-${index}`;
const isVccmTaTeam = (value) => normalizeText(value).replace(/\s+/g, ' ').includes(normalizeText(VCCM_TA_TEAM_NAME));

const taRowMatchesSelectedCountry = (row, matchValues, taCountryColumnKey, taIso3ColumnKey) => {
  if (!matchValues.size) return false;

  const candidates = [
    getRowText(row, taCountryColumnKey),
    getRowText(row, taIso3ColumnKey)
  ]
    .map(normalizeText)
    .filter(Boolean);

  return candidates.some((value) => matchValues.has(value));
};

function VCCM({
  countryRows = [],
  evmaMetricRows = [],
  isEvmaMetricsLoading = false,
  isCountryMatrixLoading = false,
  isStockMetricsLoading = false,
  isTssLoading = false,
  stockMetricRows = [],
  tssRows = []
}) {
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [countryFilter, setCountryFilter] = useState('gavi');
  const [selectedCountryKey, setSelectedCountryKey] = useState('');
  const [taRows, setTaRows] = useState([]);
  const [taColumns, setTaColumns] = useState([]);
  const [isTaLoading, setIsTaLoading] = useState(true);
  const [taError, setTaError] = useState('');
  const [expandedTaRequestKeys, setExpandedTaRequestKeys] = useState({});
  const [cceRows, setCceRows] = useState([]);
  const [isCceLoading, setIsCceLoading] = useState(true);
  const [stockAvailabilityRows, setStockAvailabilityRows] = useState([]);
  const [isStockAvailabilityLoading, setIsStockAvailabilityLoading] = useState(true);
  const taDatasetId = process.env.REACT_APP_COE_TA_TRACKER_DATASET_ID || '1ad5473d-bb83-4e5b-9980-7424a841f9b6';
  const taWorkspaceId = process.env.REACT_APP_COE_TA_TRACKER_WORKSPACE_ID || '7d441575-4aa9-4260-99dd-543f002c0340';
  const taDaxQuery = process.env.REACT_APP_COE_TA_TRACKER_DAX_QUERY || '';
  const taTableName = process.env.REACT_APP_COE_TA_TRACKER_TABLE_NAME || 'TARequests';

  useEffect(() => {
    let mounted = true;

    const loadTaRequests = async () => {
      setIsTaLoading(true);
      setTaError('');

      try {
        const effectiveQuery = String(taDaxQuery || '').trim() || undefined;
        const effectiveTableName = effectiveQuery ? undefined : (String(taTableName || '').trim() || undefined);
        const data = await fetchPowerBISemanticData(
          taDatasetId,
          taWorkspaceId,
          effectiveQuery,
          5000,
          effectiveTableName
        );

        if (!mounted) return;
        setTaRows(Array.isArray(data?.data) ? data.data : []);
        setTaColumns(Array.isArray(data?.columns) ? data.columns : []);
      } catch (loadError) {
        if (!mounted) return;
        setTaRows([]);
        setTaColumns([]);
        setTaError('TA Hub requests are unavailable right now.');
      } finally {
        if (mounted) setIsTaLoading(false);
      }
    };

    loadTaRequests();

    return () => {
      mounted = false;
    };
  }, [taDatasetId, taWorkspaceId, taDaxQuery, taTableName]);

  useEffect(() => {
    let mounted = true;

    const loadCceEquipment = async () => {
      setIsCceLoading(true);
      try {
        const idToken = await authService.getIdToken();
        const response = await fetch(`${API_BASE}/db-query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({
            query: `
              SELECT
                iso3,
                COUNT(*) AS cce_inventory_records,
                SUM(CASE WHEN LOWER(TRIM(operational_status)) = 'functional' THEN 1 ELSE 0 END) AS functional_equipment_count
              FROM ${GAVI_INVENTORY_TABLE}
              GROUP BY iso3;
            `
          }),
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`CCE inventory request failed (${response.status})`);
        }

        const payload = await response.json();
        const rows = Array.isArray(payload?.result) ? payload.result : (Array.isArray(payload) ? payload : []);
        if (!mounted) return;
        setCceRows(rows);
      } catch (loadError) {
        if (!mounted) return;
        setCceRows([]);
      } finally {
        if (mounted) setIsCceLoading(false);
      }
    };

    loadCceEquipment();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadStockAvailability = async () => {
      setIsStockAvailabilityLoading(true);
      try {
        const idToken = await authService.getIdToken();
        const response = await fetch(`${API_BASE}/db-query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({
            query: `
              SELECT
                v.iso3,
                v.Country_Name,
                v.Report_Month,
                v.Rolling_6_Month_Availability
              FROM thrive_appdb_prd.vw_district_stock_availability_6m v
              INNER JOIN (
                SELECT iso3, MAX(Report_Month) AS Report_Month
                FROM thrive_appdb_prd.vw_district_stock_availability_6m
                GROUP BY iso3
              ) latest
                ON latest.iso3 = v.iso3
               AND latest.Report_Month = v.Report_Month;
            `
          }),
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`Stock availability request failed (${response.status})`);
        }

        const payload = await response.json();
        const rows = Array.isArray(payload?.result) ? payload.result : (Array.isArray(payload) ? payload : []);
        if (!mounted) return;
        setStockAvailabilityRows(rows);
      } catch (loadError) {
        if (!mounted) return;
        setStockAvailabilityRows([]);
      } finally {
        if (mounted) setIsStockAvailabilityLoading(false);
      }
    };

    loadStockAvailability();

    return () => {
      mounted = false;
    };
  }, []);

  const stockMetricsByCountry = useMemo(() => {
    const metricsByCountry = new Map();

    stockMetricRows.forEach((row) => {
      const iso3 = normalizeKey(getRowValue(row, ['iso3', 'ISO3', 'countryCode', 'country_code']));
      const countryName = normalizeKey(getRowValue(row, ['countryName', 'Country_Name', 'country', 'Country']));
      const metrics = {
        gaviCountry: getRowValue(row, ['gaviCountry', 'Gavi_Country', 'gavi_country']),
        reportingRate: getRowValue(row, ['reportingRate', 'Reporting_Rate__', 'Reporting_Rate']),
        reportingPeriod: getRowValue(row, ['reportingPeriod', 'Reporting_Period__', 'Reporting_Period']),
        stockoutStores: getRowValue(row, ['stockoutStores', 'Stockout_Stores__', 'stockout_stores'])
      };

      if (iso3) metricsByCountry.set(iso3, metrics);
      if (countryName) metricsByCountry.set(countryName, metrics);
    });

    return metricsByCountry;
  }, [stockMetricRows]);

  const cceMetricsByCountry = useMemo(() => {
    const metricsByCountry = new Map();

    cceRows.forEach((row) => {
      const iso3 = normalizeKey(row?.iso3 || row?.ISO3);
      if (!iso3) return;
      metricsByCountry.set(iso3, {
        functionalEquipmentCount: parseMetricNumber(row?.functional_equipment_count),
        cceEquipmentCount: parseMetricNumber(row?.cce_inventory_records)
      });
    });

    return metricsByCountry;
  }, [cceRows]);

  const stockAvailabilityByCountry = useMemo(() => {
    const metricsByCountry = new Map();

    stockAvailabilityRows.forEach((row) => {
      const iso3 = normalizeKey(row?.iso3 || row?.ISO3);
      const countryName = normalizeKey(row?.Country_Name || row?.country_name || row?.CountryName);
      const metrics = {
        rollingAvailability: parseMetricNumber(
          row?.Rolling_6_Month_Availability ?? row?.rolling_6_month_availability
        ),
        reportMonth: row?.Report_Month || row?.report_month || null
      };

      if (iso3) metricsByCountry.set(iso3, metrics);
      if (countryName) metricsByCountry.set(countryName, metrics);
    });

    return metricsByCountry;
  }, [stockAvailabilityRows]);

  const evmaMetricsByCountry = useMemo(() => {
    const metricsByCountry = new Map();

    evmaMetricRows.forEach((row) => {
      const iso3 = normalizeKey(row?.iso3 || row?.ISO3 || row?.IsoCode);
      const countryName = normalizeKey(row?.countryName || row?.CountryName || row?.Country);
      const metrics = {
        evmScore: row?.evmScore,
        nextAssessmentDate: row?.nextAssessmentDate
      };

      if (iso3) metricsByCountry.set(iso3, metrics);
      if (countryName) metricsByCountry.set(countryName, metrics);
    });

    return metricsByCountry;
  }, [evmaMetricRows]);

  const tssByCountry = useMemo(() => {
    const systemsByCountry = new Map();

    tssRows.forEach((row) => {
      const iso3 = normalizeKey(row?.iso3);
      const countryName = normalizeKey(row?.country);
      const vaccineTss = String(row?.vax_tss || '').trim();
      const otherTss = String(row?.other_tss || '').trim();
      const systems = [vaccineTss, otherTss]
        .filter(Boolean)
        .filter((value, index, values) => values.findIndex((item) => item.toLowerCase() === value.toLowerCase()) === index);
      const metrics = {
        hasTss: systems.length > 0,
        systems
      };

      if (iso3) systemsByCountry.set(iso3, metrics);
      if (countryName) systemsByCountry.set(countryName, metrics);
    });

    return systemsByCountry;
  }, [tssRows]);

  const matrixRows = useMemo(() => {
    const countriesByKey = new Map();

    countryRows.forEach((country) => {
      const iso3 = String(country?.iso3 || '').trim().toUpperCase();
      const countryName = String(country?.countryName || country?.Country_Name || iso3 || '').trim();
      const key = iso3 || countryName;
      if (!key || countriesByKey.has(key)) return;

      countriesByKey.set(key, {
        key,
        iso3,
        countryName,
        isGaviCountry: country.isGaviCountry === true,
        evmaMetrics: evmaMetricsByCountry.get(iso3) || evmaMetricsByCountry.get(normalizeKey(countryName)) || null,
        stockMetrics: stockMetricsByCountry.get(iso3) || stockMetricsByCountry.get(normalizeKey(countryName)) || null,
        cceMetrics: cceMetricsByCountry.get(iso3) || null,
        stockAvailability: stockAvailabilityByCountry.get(iso3) || stockAvailabilityByCountry.get(normalizeKey(countryName)) || null,
        tssMetrics: tssByCountry.get(iso3) || tssByCountry.get(normalizeKey(countryName)) || null
      });
    });

    return Array.from(countriesByKey.values()).sort((a, b) => a.countryName.localeCompare(b.countryName));
  }, [countryRows, cceMetricsByCountry, evmaMetricsByCountry, stockAvailabilityByCountry, stockMetricsByCountry, tssByCountry]);

  const filteredMatrixRows = useMemo(() => {
    return matrixRows.filter((country) => {
      const gaviValue = normalizeKey(country.stockMetrics?.gaviCountry);
      const isGaviCountry = gaviValue ? gaviValue === 'YES' : country.isGaviCountry;
      if (countryFilter === 'gavi') return isGaviCountry;
      if (countryFilter === 'non-gavi') return !isGaviCountry;
      return true;
    });
  }, [countryFilter, matrixRows]);

  const visibleMatrixRows = showAllCountries
    ? filteredMatrixRows
    : filteredMatrixRows.slice(0, VCCM_MATRIX_PREVIEW_LIMIT);

  useEffect(() => {
    if (filteredMatrixRows.length === 0) {
      if (selectedCountryKey) setSelectedCountryKey('');
      return;
    }

    const countryKeys = filteredMatrixRows.map((country) => country.key);
    if (!countryKeys.includes(selectedCountryKey)) {
      setSelectedCountryKey(countryKeys[0]);
    }
  }, [filteredMatrixRows, selectedCountryKey]);

  const selectedCountry = useMemo(
    () => filteredMatrixRows.find((country) => country.key === selectedCountryKey) || null,
    [filteredMatrixRows, selectedCountryKey]
  );

  const taLeadCoeTeamColumnKey = useMemo(
    () => findColumnKey(taColumns, ['lead coe team (accountable)', 'lead coe team', 'lead coe', 'coe team']),
    [taColumns]
  );
  const taCountryColumnKey = useMemo(
    () => findColumnKey(taColumns, ['country']),
    [taColumns]
  );
  const taIso3ColumnKey = useMemo(
    () => findColumnKey(taColumns, ['iso3', 'country iso3', 'country_iso3', 'country code', 'country iso']),
    [taColumns]
  );
  const taTitleColumnKey = useMemo(
    () => findColumnKey(taColumns, [
      'request title',
      'request',
      'title',
      'activity',
      'intervention',
      'action',
      'issue'
    ]),
    [taColumns]
  );
  const taDescriptionColumnKey = useMemo(
    () => findColumnKey(taColumns, ['description', 'request description']),
    [taColumns]
  );
  const taStatusColumnKey = useMemo(
    () => findColumnKey(taColumns, ['implementation status', 'status']),
    [taColumns]
  );
  const taLeadStaffColumnKey = useMemo(
    () => findColumnKey(taColumns, ['ta lead staff', 'ta lead staff (accountable)']),
    [taColumns]
  );
  const selectedCountryMatchValues = useMemo(() => {
    const values = new Set();
    if (!selectedCountry) return values;
    const countryName = normalizeText(selectedCountry.countryName);
    const iso3 = normalizeText(selectedCountry.iso3);
    if (countryName) values.add(countryName);
    if (iso3) values.add(iso3);
    return values;
  }, [selectedCountry]);
  const vccmTaRequests = useMemo(() => {
    const teamRows = taRows.filter((row) => isVccmTaTeam(getRowText(row, taLeadCoeTeamColumnKey)));
    if (!selectedCountry) return [];

    return teamRows.filter((row) => (
      taRowMatchesSelectedCountry(row, selectedCountryMatchValues, taCountryColumnKey, taIso3ColumnKey)
    ));
  }, [
    taRows,
    taLeadCoeTeamColumnKey,
    selectedCountry,
    selectedCountryMatchValues,
    taCountryColumnKey,
    taIso3ColumnKey
  ]);

  const toggleTaRequestExpanded = (requestKey) => {
    setExpandedTaRequestKeys((current) => ({
      ...current,
      [requestKey]: !current[requestKey]
    }));
  };

  useEffect(() => {
    setExpandedTaRequestKeys({});
  }, [selectedCountryKey]);

  return (
    <section className="home-section vaccines-page-section vaccines-dashboard">
      <div className="vaccines-dashboard-header">
        <div>
          <h2 className="vaccines-dashboard-title">Vaccine and Cold Chain Management</h2>
          <p className="vaccines-dashboard-subtitle">
            Stock performance, cold chain, delivery and country instance tools.
          </p>
        </div>
      </div>

      <div className="vaccines-schedule-card vaccines-matrix-card">
        <div className="vaccines-card-header">
          <div>
            <p className="vaccines-section-label">Country Matrix</p>
            <h3 className="vaccines-card-title">VCCM country performance overview</h3>
            <p className="budgets-data-note">Stock reporting rate uses the last month with data for each country.</p>
          </div>
          <div className="vaccines-segmented-control" aria-label="Country funding filter">
            <button
              type="button"
              className={countryFilter === 'gavi' ? 'is-active' : ''}
              onClick={() => {
                setCountryFilter('gavi');
                setShowAllCountries(false);
              }}
            >
              Gavi Countries
            </button>
            <button
              type="button"
              className={countryFilter === 'non-gavi' ? 'is-active' : ''}
              onClick={() => {
                setCountryFilter('non-gavi');
                setShowAllCountries(false);
              }}
            >
              Non-Gavi Countries
            </button>
            <button
              type="button"
              className={countryFilter === 'all' ? 'is-active' : ''}
              onClick={() => {
                setCountryFilter('all');
                setShowAllCountries(false);
              }}
            >
              All
            </button>
          </div>
        </div>

        <div className="vaccines-matrix-wrap">
          <table className="vaccines-matrix-table vccm-country-matrix" aria-label="VCCM country matrix">
            <colgroup>
              <col className="vccm-country-col" />
              <col className="vccm-rate-col" />
              <col className="vccm-availability-col" />
              <col className="vccm-stockout-col" />
              <col className="vccm-evma-col" />
              <col className="vccm-tss-col" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col">Country name</th>
                <th scope="col">Stock Reporting rate</th>
                <th scope="col">Stock full availability</th>
                <th scope="col">Functional Equipment</th>
                <th scope="col">EVMA Score and next assessment</th>
                <th scope="col">TSS system implemented and system name</th>
              </tr>
            </thead>
            <tbody>
              {isCountryMatrixLoading && (
                <tr>
                  <td colSpan="6">Loading country matrix...</td>
                </tr>
              )}
              {!isCountryMatrixLoading && visibleMatrixRows.length === 0 && (
                <tr>
                  <td colSpan="6">No countries found for this view.</td>
                </tr>
              )}
              {!isCountryMatrixLoading && visibleMatrixRows.map((country) => (
                <tr
                  key={country.key}
                  className={country.key === selectedCountryKey ? 'is-selected' : ''}
                  aria-selected={country.key === selectedCountryKey}
                >
                  <th scope="row">
                    <button type="button" onClick={() => setSelectedCountryKey(country.key)}>
                      {country.countryName}
                    </button>
                    {country.iso3 && <span className="summary-country-code">{country.iso3}</span>}
                  </th>
                  <td>
                    <div className="journey-main-cell">
                      <span className={`vaccines-table-pill ${formatPercent(country.stockMetrics?.reportingRate) ? '' : 'is-empty'}`}>
                        {isStockMetricsLoading ? 'Loading...' : formatPercent(country.stockMetrics?.reportingRate) || 'No data'}
                      </span>
                      {!isStockMetricsLoading && country.stockMetrics?.reportingPeriod && (
                        <small>{String(country.stockMetrics.reportingPeriod)}</small>
                      )}
                    </div>
                  </td>
                  <td>
                    {isStockAvailabilityLoading ? (
                      <span className="vaccines-table-pill is-empty">Loading...</span>
                    ) : formatPercent(country.stockAvailability?.rollingAvailability) ? (
                      <span className="vaccines-table-pill">
                        {formatPercent(country.stockAvailability.rollingAvailability)}
                      </span>
                    ) : (
                      <span className="vaccines-table-pill is-empty">No data</span>
                    )}
                  </td>
                  <td>
                    {isCceLoading ? (
                      <span className="vaccines-table-pill is-empty">Loading...</span>
                    ) : country.cceMetrics?.cceEquipmentCount > 0 ? (
                      <div className="journey-main-cell">
                        <span className="vaccines-table-pill">
                          {formatNumber(country.cceMetrics.functionalEquipmentCount) || '0'}
                        </span>
                        <small>
                          {`${((Number(country.cceMetrics.functionalEquipmentCount || 0) / Number(country.cceMetrics.cceEquipmentCount)) * 100).toFixed(1)}% functional`}
                        </small>
                      </div>
                    ) : (
                      <span className="vaccines-table-pill is-empty">No data</span>
                    )}
                  </td>
                  <td>
                    {isEvmaMetricsLoading ? (
                      <span className="vaccines-table-pill is-empty">Loading...</span>
                    ) : country.evmaMetrics?.evmScore || country.evmaMetrics?.nextAssessmentDate ? (
                      <div className="journey-main-cell">
                        <span className={`vaccines-table-pill ${country.evmaMetrics?.evmScore ? '' : 'is-empty'}`}>
                          {formatScore(country.evmaMetrics?.evmScore) || 'No score'}
                        </span>
                        {country.evmaMetrics?.nextAssessmentDate && (
                          <small>Next EVMA: {formatDate(country.evmaMetrics.nextAssessmentDate)}</small>
                        )}
                      </div>
                    ) : (
                      <span className="vaccines-table-pill is-empty">No EVMA data</span>
                    )}
                  </td>
                  <td>
                    {isTssLoading ? (
                      <span className="vaccines-table-pill is-empty">Loading...</span>
                    ) : country.tssMetrics?.hasTss ? (
                      <div className="journey-main-cell">
                        <span className="vaccines-table-pill">Implemented</span>
                        <small>{country.tssMetrics.systems.join(', ')}</small>
                      </div>
                    ) : (
                      <span className="vaccines-table-pill is-empty">No TSS data</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredMatrixRows.length > VCCM_MATRIX_PREVIEW_LIMIT && (
          <div className="vaccines-matrix-footer">
            <p className="vaccines-footnote">
              Showing {visibleMatrixRows.length} of {filteredMatrixRows.length} countries in this view.
            </p>
            <button
              type="button"
              className="vaccines-expand-button"
              onClick={() => setShowAllCountries((current) => !current)}
            >
              {showAllCountries ? 'Show fewer countries' : `Show all ${filteredMatrixRows.length} countries`}
            </button>
          </div>
        )}
      </div>

      <div className="vaccines-schedule-card budgets-ta-card">
        <div className="vaccines-card-header">
          <div>
            <p className="vaccines-section-label">Targeted TA</p>
            <h3 className="vaccines-card-title">Immunization - VCCM TA Hub requests</h3>
          </div>
          <p className="budgets-data-note">
            {isTaLoading
              ? 'Loading TA Hub requests...'
              : !selectedCountry
                ? 'Select a country in the matrix above'
                : `${vccmTaRequests.length} request${vccmTaRequests.length === 1 ? '' : 's'} for ${selectedCountry.countryName}`}
          </p>
        </div>

        {taError && <p className="budgets-error-note">{taError}</p>}
        {!isTaLoading && !taError && !selectedCountry && (
          <p className="budgets-data-note">Click a country in the matrix above to view Immunization - VCCM TA requests for that country.</p>
        )}

        <div className="vaccines-wuenic-table-wrap budgets-table-wrap">
          <table className="vaccines-wuenic-table budgets-table budgets-ta-table" aria-label="Immunization - VCCM TA Hub requests">
            <thead>
              <tr>
                <th scope="col">Country</th>
                <th scope="col">Description</th>
                <th scope="col">Status</th>
                <th scope="col">TA Lead Staff</th>
                <th scope="col">Lead CoE Team</th>
              </tr>
            </thead>
            <tbody>
              {isTaLoading && (
                <tr>
                  <td colSpan="5">Loading Immunization - VCCM TA requests...</td>
                </tr>
              )}
              {!isTaLoading && !taError && selectedCountry && vccmTaRequests.length === 0 && (
                <tr>
                  <td colSpan="5">No Immunization - VCCM TA requests found for {selectedCountry.countryName}.</td>
                </tr>
              )}
              {!isTaLoading && vccmTaRequests.map((row, index) => {
                const requestKey = getTaRequestKey(row, index, taTitleColumnKey);
                const description = getRowText(row, taDescriptionColumnKey) || getRowText(row, taTitleColumnKey);
                const isExpanded = Boolean(expandedTaRequestKeys[requestKey]);
                const shouldTruncate = description.length > 200;
                const visibleDescription = shouldTruncate && !isExpanded
                  ? `${description.slice(0, 200).trimEnd()}...`
                  : description;

                return (
                  <tr key={requestKey}>
                    <td>{getRowText(row, taCountryColumnKey) || 'N/A'}</td>
                    <td>
                      {visibleDescription || 'N/A'}
                      {shouldTruncate && (
                        <button
                          type="button"
                          className="budgets-ta-read-more-button"
                          onClick={() => toggleTaRequestExpanded(requestKey)}
                        >
                          {isExpanded ? 'Show less' : 'Read more'}
                        </button>
                      )}
                    </td>
                    <td>{getRowText(row, taStatusColumnKey) || 'N/A'}</td>
                    <td>{getRowText(row, taLeadStaffColumnKey) || 'N/A'}</td>
                    <td>
                      <span className="budgets-ta-priority">{getRowText(row, taLeadCoeTeamColumnKey) || VCCM_TA_TEAM_NAME}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default VCCM;
